import { useState, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Plus, Pencil, Trash2, ArrowLeft, Upload, X, Check, ImageIcon, Star,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { getProducts, addProduct, updateProduct, deleteProduct, Product } from '@/lib/store';
import { useToast } from '@/hooks/useToast';

const CATEGORIES = ['hunting', 'fishing', 'archery', 'apparel', 'camping', 'accessories'];
const BADGES = ['', 'Best Seller', 'New Arrival', 'Staff Pick', 'Top Rated', 'Sale', 'New'];

// ── Product List ────────────────────────────────────────────────────────────
export function AdminProductList() {
  const [products, setProducts] = useState(() => getProducts());
  const [search, setSearch] = useState('');
  const { toast } = useToast();

  const filtered = products.filter((p) =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    p.category.toLowerCase().includes(search.toLowerCase())
  );

  function handleDelete(id: string) {
    deleteProduct(id);
    setProducts(getProducts());
    toast({ title: 'Product deleted' });
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white">Products</h1>
          <p className="text-stone-400 text-sm">{products.length} total products</p>
        </div>
        <Button asChild className="bg-amber-600 hover:bg-amber-500 text-white rounded-sm">
          <Link to="/admin/products/new"><Plus className="h-4 w-4 mr-1.5" /> Add Product</Link>
        </Button>
      </div>

      {/* Search */}
      <Input
        placeholder="Search products…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="bg-[hsl(25,20%,12%)] border-white/10 text-white placeholder:text-stone-600 max-w-xs"
      />

      {/* Table / cards */}
      <div className="hidden md:block">
        <Card className="bg-[hsl(25,20%,12%)] border-white/10 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left px-4 py-3 text-stone-400 font-medium">Product</th>
                  <th className="text-left px-4 py-3 text-stone-400 font-medium">Category</th>
                  <th className="text-left px-4 py-3 text-stone-400 font-medium">Price</th>
                  <th className="text-left px-4 py-3 text-stone-400 font-medium">Status</th>
                  <th className="text-left px-4 py-3 text-stone-400 font-medium">Featured</th>
                  <th className="text-right px-4 py-3 text-stone-400 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((product) => (
                  <tr key={product.id} className="border-b border-white/5 hover:bg-white/3 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-md bg-stone-800 flex items-center justify-center overflow-hidden shrink-0">
                          {product.imageUrl ? (
                            <img src={product.imageUrl} alt={product.name} className="h-full w-full object-cover" />
                          ) : (
                            <ImageIcon className="h-4 w-4 text-stone-600" />
                          )}
                        </div>
                        <div>
                          <p className="text-stone-200 font-medium">{product.name}</p>
                          {product.badge && <Badge className="bg-amber-800/40 text-amber-300 border-0 text-[10px] px-1.5 mt-0.5">{product.badge}</Badge>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-stone-400 capitalize">{product.category}</td>
                    <td className="px-4 py-3 text-amber-400 font-semibold">${product.price.toFixed(2)}</td>
                    <td className="px-4 py-3">
                      <Badge className={product.inStock
                        ? 'bg-green-900/40 text-green-400 border-0'
                        : 'bg-red-900/40 text-red-400 border-0'
                      }>
                        {product.inStock ? 'In Stock' : 'Out of Stock'}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      {product.featured ? <Star className="h-4 w-4 fill-amber-500 text-amber-500" /> : <span className="text-stone-700">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <Button asChild size="sm" variant="ghost" className="text-stone-400 hover:text-white h-7 w-7 p-0">
                          <Link to={`/admin/products/${product.id}`}><Pencil className="h-3.5 w-3.5" /></Link>
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="sm" variant="ghost" className="text-stone-600 hover:text-red-400 h-7 w-7 p-0">
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="bg-[hsl(25,20%,12%)] border-white/10">
                            <AlertDialogHeader>
                              <AlertDialogTitle className="text-white">Delete Product?</AlertDialogTitle>
                              <AlertDialogDescription className="text-stone-400">
                                This will permanently remove "{product.name}". This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel className="bg-transparent border-white/10 text-stone-300">Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleDelete(product.id)} className="bg-red-700 hover:bg-red-600 text-white">
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center text-stone-500">No products found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {filtered.map((product) => (
          <Card key={product.id} className="bg-[hsl(25,20%,12%)] border-white/10">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="h-14 w-14 rounded-md bg-stone-800 flex items-center justify-center overflow-hidden shrink-0">
                {product.imageUrl ? (
                  <img src={product.imageUrl} alt={product.name} className="h-full w-full object-cover" />
                ) : (
                  <ImageIcon className="h-5 w-5 text-stone-600" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-stone-200 font-medium text-sm truncate">{product.name}</p>
                <p className="text-stone-500 text-xs capitalize">{product.category}</p>
                <p className="text-amber-400 font-semibold text-sm">${product.price.toFixed(2)}</p>
              </div>
              <div className="flex gap-1">
                <Button asChild size="sm" variant="ghost" className="text-stone-400 hover:text-white h-8 w-8 p-0">
                  <Link to={`/admin/products/${product.id}`}><Pencil className="h-4 w-4" /></Link>
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="sm" variant="ghost" className="text-stone-600 hover:text-red-400 h-8 w-8 p-0">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="bg-[hsl(25,20%,12%)] border-white/10">
                    <AlertDialogHeader>
                      <AlertDialogTitle className="text-white">Delete Product?</AlertDialogTitle>
                      <AlertDialogDescription className="text-stone-400">This cannot be undone.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="bg-transparent border-white/10 text-stone-300">Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => handleDelete(product.id)} className="bg-red-700 hover:bg-red-600 text-white">Delete</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </CardContent>
          </Card>
        ))}
        {filtered.length === 0 && (
          <p className="text-center text-stone-500 py-12">No products found.</p>
        )}
      </div>
    </div>
  );
}

// ── Product Form ─────────────────────────────────────────────────────────────
export function AdminProductForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const { toast } = useToast();

  const existing = !isNew ? getProducts().find((p) => p.id === id) : undefined;

  const [form, setForm] = useState<Omit<Product, 'id' | 'createdAt'>>({
    name: existing?.name ?? '',
    description: existing?.description ?? '',
    price: existing?.price ?? 0,
    originalPrice: existing?.originalPrice,
    category: existing?.category ?? 'hunting',
    badge: existing?.badge ?? '',
    imageUrl: existing?.imageUrl ?? '',
    inStock: existing?.inStock ?? true,
    featured: existing?.featured ?? false,
  });

  const [imagePreview, setImagePreview] = useState<string>(existing?.imageUrl ?? '');
  const [saving, setSaving] = useState(false);

  function handleChange(field: keyof typeof form, value: string | number | boolean | undefined) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  const handleImageUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target?.result as string;
      setImagePreview(dataUrl);
      handleChange('imageUrl', dataUrl);
    };
    reader.readAsDataURL(file);
  }, []);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      if (isNew) {
        addProduct(form);
        toast({ title: 'Product added successfully' });
      } else {
        updateProduct(id, form);
        toast({ title: 'Product updated successfully' });
      }
      navigate('/admin/products');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="max-w-2xl space-y-6">
      <div className="flex items-center gap-3">
        <Button asChild variant="ghost" size="sm" className="text-stone-400 hover:text-white">
          <Link to="/admin/products"><ArrowLeft className="h-4 w-4 mr-1" /> Products</Link>
        </Button>
        <h1 className="text-2xl font-bold text-white">{isNew ? 'Add Product' : 'Edit Product'}</h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        <Card className="bg-[hsl(25,20%,12%)] border-white/10">
          <CardHeader><CardTitle className="text-white text-base">Product Details</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            {/* Name */}
            <div className="space-y-1.5">
              <Label className="text-stone-300">Product Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="e.g. Dakota Ridge Hunting Vest"
                required
                className="bg-[hsl(25,20%,8%)] border-white/10 text-white placeholder:text-stone-600"
              />
            </div>

            {/* Description */}
            <div className="space-y-1.5">
              <Label className="text-stone-300">Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Describe this product…"
                rows={3}
                className="bg-[hsl(25,20%,8%)] border-white/10 text-white placeholder:text-stone-600 resize-none"
              />
            </div>

            {/* Price row */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-stone-300">Price ($) *</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.price}
                  onChange={(e) => handleChange('price', parseFloat(e.target.value) || 0)}
                  required
                  className="bg-[hsl(25,20%,8%)] border-white/10 text-white"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-stone-300">Original Price ($) <span className="text-stone-500 text-xs">optional</span></Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.originalPrice ?? ''}
                  onChange={(e) => handleChange('originalPrice', e.target.value ? parseFloat(e.target.value) : undefined)}
                  className="bg-[hsl(25,20%,8%)] border-white/10 text-white"
                />
              </div>
            </div>

            {/* Category + Badge */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-stone-300">Category *</Label>
                <select
                  value={form.category}
                  onChange={(e) => handleChange('category', e.target.value)}
                  className="w-full h-9 rounded-md border border-white/10 bg-[hsl(25,20%,8%)] text-white px-3 text-sm focus:outline-none focus:ring-1 focus:ring-amber-600"
                >
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c} className="bg-[hsl(25,20%,8%)] capitalize">{c.charAt(0).toUpperCase() + c.slice(1)}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-stone-300">Badge <span className="text-stone-500 text-xs">optional</span></Label>
                <select
                  value={form.badge ?? ''}
                  onChange={(e) => handleChange('badge', e.target.value)}
                  className="w-full h-9 rounded-md border border-white/10 bg-[hsl(25,20%,8%)] text-white px-3 text-sm focus:outline-none focus:ring-1 focus:ring-amber-600"
                >
                  {BADGES.map((b) => (
                    <option key={b} value={b} className="bg-[hsl(25,20%,8%)]">{b || '— None —'}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Toggles */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex items-center justify-between sm:justify-start gap-3 bg-[hsl(25,20%,8%)] rounded-md px-4 py-3 flex-1">
                <Label className="text-stone-300 cursor-pointer">In Stock</Label>
                <Switch
                  checked={form.inStock}
                  onCheckedChange={(v) => handleChange('inStock', v)}
                  className="data-[state=checked]:bg-green-600"
                />
              </div>
              <div className="flex items-center justify-between sm:justify-start gap-3 bg-[hsl(25,20%,8%)] rounded-md px-4 py-3 flex-1">
                <Label className="text-stone-300 cursor-pointer">Featured on Homepage</Label>
                <Switch
                  checked={form.featured}
                  onCheckedChange={(v) => handleChange('featured', v)}
                  className="data-[state=checked]:bg-amber-600"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Image Upload */}
        <Card className="bg-[hsl(25,20%,12%)] border-white/10">
          <CardHeader><CardTitle className="text-white text-base">Product Image</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            {imagePreview ? (
              <div className="relative w-48 h-48">
                <img src={imagePreview} alt="Preview" className="w-full h-full object-cover rounded-md border border-white/10" />
                <button
                  type="button"
                  onClick={() => { setImagePreview(''); handleChange('imageUrl', ''); }}
                  className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full h-6 w-6 flex items-center justify-center hover:bg-red-500 transition-colors"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-white/15 rounded-md cursor-pointer hover:border-amber-600/50 transition-colors group">
                <Upload className="h-8 w-8 text-stone-600 group-hover:text-amber-600 transition-colors mb-2" />
                <p className="text-stone-400 text-sm">Click to upload product image</p>
                <p className="text-stone-600 text-xs mt-1">JPG, PNG, WEBP up to 5MB</p>
                <input type="file" accept="image/*" onChange={handleImageUpload} className="sr-only" />
              </label>
            )}

            <div className="space-y-1.5">
              <Label className="text-stone-300 text-xs">Or paste an image URL</Label>
              <Input
                value={form.imageUrl ?? ''}
                onChange={(e) => {
                  handleChange('imageUrl', e.target.value);
                  setImagePreview(e.target.value);
                }}
                placeholder="https://example.com/product.jpg"
                className="bg-[hsl(25,20%,8%)] border-white/10 text-white placeholder:text-stone-600 text-sm"
              />
            </div>
          </CardContent>
        </Card>

        <div className="flex gap-3">
          <Button
            type="submit"
            disabled={saving}
            className="bg-amber-600 hover:bg-amber-500 text-white rounded-sm px-6"
          >
            <Check className="h-4 w-4 mr-1.5" />
            {saving ? 'Saving…' : isNew ? 'Add Product' : 'Save Changes'}
          </Button>
          <Button asChild variant="outline" className="border-white/10 text-stone-300 hover:text-white rounded-sm">
            <Link to="/admin/products">Cancel</Link>
          </Button>
        </div>
      </form>
    </div>
  );
}
